import Vue from 'vue';
import Vuex from 'vuex';

import api from '@/api';
import router from '@/app/app-routes';

import breakpoints from '@/app/store/_breakpoints';
import country from '@/app/store/_country';
import headhunterSearch from '@/app/store/_headhunter-search';
import headhunter from '@/app/store/_headhunter';
import headhunterBookmark from '@/app/store/_headhunter-bookmark';
import jobState from '@/app/pages/job/_id-state';
import jobSearch from '@/app/store/_job-search';
import jobBookmark from '@/app/store/_job-bookmark';
import jobLastSearch from '@/app/store/_job-last-search';
import jobSavedSearch from '@/app/store/_job-saved-search';
import jobSearchAnimation from '@/app/store/_job-search-animation';
import mobileBackButton from '@/app/store/_mobile-back-button';
import masterClass from '@/app/store/_masterclass';
import profession from '@/app/store/_profession';
import quiz from '@/app/store/_quiz';
import resume from '@/app/store/_resume';
import salary from '@/app/store/_salary';
import userStore from '@/app/store/_user';
import smartApply from '@/app/store/_smart-apply';
import resumeBuilder from '@/app/store/_resume-builder';

Vue.use(Vuex);

const apiPlugin = (store) => {
  store.$api = api;
};
const TOP_BRAND_RESUME_LOCALES = ['US', 'CA', 'AU', 'SG', 'IN', 'PH', 'MY'];

export default new Vuex.Store({
  state: {
    locale: null,
    appLocale: null,
    data: null,
    lastInternalRoute: null,
    isWebpSupported: false,
    redirectUrlAfterLogin: null,
    showShopOverlay: false,
    shopOverlayLink: null,
  },
  mutations: {
    setData(currentState, newValue) {
      Object.assign(currentState, {
        locale: newValue?.localization?.dictionaryLocale || 'en_US',
        appLocale: newValue?.localization?.appLocale || 'en-US',
        data: newValue,
      });
    },

    setLastInternalRoute(currentState, value) {
      currentState.lastInternalRoute = value;
    },

    setWebpSupported(currentState, value) {
      currentState.isWebpSupported = value;
    },
    setRedirectUrlAfterLogin(currentState, value) {
      currentState.redirectUrlAfterLogin = value;
    },
    setShowShopOverlay(currentState, value) {
      currentState.showShopOverlay = value;
    },
    setShopOverlayLink(currentState, value) {
      currentState.shopOverlayLink = value;
    },
  },
  actions: {
    init({ commit }, data) {
      commit('setData', data);
    },
    // TODO: move it to job store after refactor
    sendJobTracking({ getters, rootGetters }, payload) {
      const { id } = payload;
      if (!id) {
        return;
      }

      const routeName = router.currentRoute.name;

      const search_types = {
        'search-jobs-slug': 'SEO',
        'search-headhunter-jobs-slug': 'SEO',
        'search-jobs-headhunter': 'SEO',
        'headhunter-preview-external': 'HHProfilePageSEO',
        'headhunter-preview': 'HHProfilePage',
      };

      const mid = id.substring(1);
      const isMobile = getters['breakpoints/isMobile'] ? 1 : 0;

      let search_type = search_types[routeName]
        ? search_types[routeName]
        : rootGetters['user/isLoggedIn']
          ? 'Internal'
          : 'External';
      if (payload.fromBookmark) {
        search_type = 'BookmarkedJobs';
      }

      const from_saved_search =
        (payload.from_saved_search || router.currentRoute.query.saved_search_id) &&
          !payload.fromBookmark
          ? 1
          : 0;

      let link = this.getters.getApiLink(
        'tracking.jobClick',
        {
          id: mid,
          action: payload.action,
        },
        {
          is_mobile: isMobile,
          search_type,
          from_saved_search,
          free: payload.isAccessible ? 1 : undefined,
        },
      );
      // @ts-ignore
      return this.$api.get(link);
    },
  },

  getters: {
    getApiLink: (s) => (key: string, data, extraParams) => {
      let result = key
        .split('.')
        .reduce((r, k) => (r ? r[k] : undefined), s.data?.links?.api || '');

      if (result && data) {
        Object.entries(data)
          .filter(([, v]) => v !== undefined)
          .forEach(([k, v]) => {
            result = result.replace(`:${k}`, encodeURIComponent(v.toString()));
          });
        result = result.replace(/\/{0,1}:.*/, '');
      }

      if (result && extraParams) {
        Object.entries(extraParams)
          .filter(([, v]) => v !== undefined)
          .forEach(([k, v]) => {
            result += result.indexOf('?') > 0 ? '&' : '?';
            result += `${k}=${encodeURIComponent(v.toString())}`;
          });
      }

      return result;
    },
    showShopOverlay: (s) => s.showShopOverlay,
    useShopOverlayCampaign: (s) => s.data?.user?.useShopOverlayCampaign,
    getShopOverlayLink: (state, getters) => () => {
      const link = state.data?.links?.navigation?.shopForOverlay;
      const jlSourcePrefix = router.currentRoute.meta.jlSourcePrefix || 'home';
      const deviceType = getters['breakpoints/isMobile'] ? 'mobile' : 'desktop';
      return `${link}?jl_source=${jlSourcePrefix}_${deviceType}_${state.shopOverlayLink}&source=iframe`;
    },
    getShopLink: (state, getters) => (payload) => {
      const link = state.data?.links?.navigation?.shop;
      if (!payload) {
        return link;
      }
      const jlSourcePrefix = router.currentRoute.meta.jlSourcePrefix || 'home';
      const deviceType = getters['breakpoints/isMobile'] ? 'mobile' : 'desktop';
      return `${link}?jl_source=${jlSourcePrefix}_${deviceType}_${payload}`;
    },
    getShopJobLink: (state, getters) => (payload) => {
      const link = state.data?.links?.navigation?.shopForJob;
      if (!payload) {
        return link;
      }
      const jlSourcePrefix = router.currentRoute.meta.jlSourcePrefix || 'home';
      const deviceType = getters['breakpoints/isMobile'] ? 'mobile' : 'desktop';
      const shopJobLink = link.replace(':jobId', payload.job);
      return `${shopJobLink}?jl_source=${jlSourcePrefix}_${deviceType}_${payload.param}`;
    },
    getNewShopLink: (state, getters) => (payload) => {
      const link = '/new/shop';
      if (!payload) {
        return link;
      }
      const jlSourcePrefix = router.currentRoute.meta.jlSourcePrefix || 'home';
      const deviceType = getters['breakpoints/isMobile'] ? 'mobile' : 'desktop';
      const shopJobLink = link.replace(':jobId', payload.job);
      return `${shopJobLink}?returnUrl=${router.currentRoute.path}?jl_source=${jlSourcePrefix}_${deviceType}_${payload.param}`;
    },
    getMailerAddress: (s) => s.data?.localization?.mailerAddress || 'mailer@jobleads.com',
    locale: (s) => s.locale,
    appLocale: (s) => s.appLocale,
    language: (s) => s.data?.localization?.language || 'en',
    languageMapping: (s) => s.data?.localization?.languageMapping || {},
    languageOptions: (s) =>
      Object.entries(s.data?.localization?.languageMapping || {}).map(([value, label]) => ({
        label,
        value,
        selected: (s.data?.localization?.language || 'en') === value,
      })),
    radiusOptions: (s) =>
      Object.entries(s.data?.localization.radiusOptions || {}).map(([value, label]) => ({
        label,
        value: Number(value),
      })),

    stats: (s) => {
      const stats = s.data?.company?.statistics || {};
      const localeNumberFormat = Vue.filter('localeNumberFormat');
      return {
        companies: localeNumberFormat(stats.companyCount),
        headhunters: localeNumberFormat(stats.headhunterCount),
        portals: localeNumberFormat(stats.jobPortalsCount),
        sources: localeNumberFormat(stats.communicatedNumberOfJobSources),
        jobs: localeNumberFormat(stats.communicatedNumberOfJobs),
      };
    },

    registration: (s) => ({
      submitUrl: s.data?.registration?.formSaveUrl,
      saveUrl: s.data?.links?.api?.registration?.save,
      images: s.data?.registration?.images,
    }),

    navigationUrlList: (s) => s.data?.links?.navigation || {},
    getCompanyData: (s) => s.data?.company || {},
    jobPins: (s) =>
      (s.data?.company?.jobPins || []).map((e) => {
        return { ...e, salary: Vue.filter('localeNumberWithCurrencyFormat')(e.salary) };
      }),
    lastInternalRoute: (s) => s.lastInternalRoute,
    userRegion: (s) => s.data?.links?.static?.regionReplacement,
    isWebpSupported: (s) => s.isWebpSupported,
    topBrand: (currentState, currentGetters, rootState, rootGetters) => {
      // @ts-ignore
      const countryCode =
        currentState.data?.localization?.countryCode || rootGetters['country/localeCountry'];
      if (TOP_BRAND_RESUME_LOCALES.includes(countryCode)) {
        return {
          // eslint-disable-next-line global-require
          img: require('@/app/assets/logo-topresume.svg'),
          alt: 'TopResume logo',
          value: 'TopResume',
          text: 'TopResume',
          terms: 'https://www.topresume.com/terms',
          privacy: 'https://www.topresume.com/privacy',
        };
      }
      return {
        // eslint-disable-next-line global-require
        img: require('@/app/assets/logo-topcv.svg'),
        alt: 'TopCV logo',
        value: 'TopCV',
        text: 'TopCV',
        terms: 'https://www.topcv.com/terms',
        privacy: 'https://www.topcv.com/privacy',
      };
    },
    redirectUrlAfterLogin: (currentState) => currentState.redirectUrlAfterLogin,
    isBot: (currentState) => currentState.data?.user?.isBot,
    refreshToken: (s) => s.data?.links?.api?.user?.refreshToken,
  },

  modules: {
    breakpoints,
    country,
    headhunterSearch,
    headhunter,
    headhunterBookmark,
    job: jobState,
    jobSearch,
    jobBookmark,
    jobLastSearch,
    jobSavedSearch,
    jobSearchAnimation,
    mobileBackButton,
    masterClass,
    profession,
    quiz,
    resume,
    salary,
    user: userStore,
    smartApply,
    resumeBuilder,
  },
  plugins: [apiPlugin],
});
